import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Eye,
  EyeOff,
  Filter,
  LockKeyhole,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';
import { ApiError } from '@/lib/api-client';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import {
  createTicketForm,
  deleteTicketForm,
  getTicketForms,
  publishTicketForm,
  unpublishTicketForm,
  type TicketForm,
} from '@/lib/ticket-form-api';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, DropdownSelect, EmptyState, ErrorState, Input, PageHeader, SkeletonRow, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, useConfirm } from '@/components/ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

function formatDate(value: string, language: string, timezone?: string | null) {
  if (!value) return '—';
  try {
    return new Intl.DateTimeFormat(language, {
      dateStyle: 'medium',
      timeStyle: 'short',
      ...(timezone ? { timeZone: timezone } : {}),
    }).format(new Date(value));
  } catch {
    return new Date(value).toLocaleString(language);
  }
}

type StatusFilter = 'all' | 'published' | 'draft';

export function TicketFormsPage() {
  const { confirm } = useConfirm();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, language } = useI18n();
  const errorMessage = React.useCallback(
    (error: unknown) => (error instanceof ApiError ? String(error.message) : t('set.requestFailed')),
    [t],
  );
  const updatedByLabel = (value: string | null) => (value ? value.slice(0, 8) : t('set.tfm.systemUser'));
  const [forms, setForms] = React.useState<TicketForm[]>([]);
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('all');
  const [showFilters, setShowFilters] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [creating, setCreating] = React.useState(false);
  const [actionId, setActionId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const loadForms = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setForms(await getTicketForms());
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [errorMessage]);

  React.useEffect(() => {
    void loadForms();
  }, [loadForms]);

  const visibleForms = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    return forms.filter((form) => {
      const matchesSearch = !query || `${form.name} ${form.description ?? ''}`.toLowerCase().includes(query);
      const matchesStatus = statusFilter === 'all'
        || (statusFilter === 'published' && form.isPublished)
        || (statusFilter === 'draft' && !form.isPublished);
      return matchesSearch && matchesStatus;
    });
  }, [forms, search, statusFilter]);

  async function createForm() {
    setCreating(true);
    setError(null);
    try {
      const form = await createTicketForm({
        name: t('set.tfm.untitled'),
        description: '',
        fields: [],
      });
      navigate(`/settings/ticket-forms/${form.id}`);
    } catch (createError) {
      setError(errorMessage(createError));
    } finally {
      setCreating(false);
    }
  }

  async function togglePublished(form: TicketForm) {
    setActionId(form.id);
    setError(null);
    try {
      const updated = form.isPublished
        ? await unpublishTicketForm(form.id)
        : await publishTicketForm(form.id);
      setForms((current) => current.map((item) => item.id === updated.id ? updated : item));
    } catch (publishError) {
      setError(errorMessage(publishError));
    } finally {
      setActionId(null);
    }
  }

  async function removeForm(form: TicketForm) {
    if (form.isDefault || !await confirm({ title: t('set.deleteFormTitle', { name: form.name }), description: t('set.deleteFormDescription'), confirmLabel: t('set.deleteForm') })) return;
    setActionId(form.id);
    setError(null);
    try {
      await deleteTicketForm(form.id);
      setForms((current) => current.filter((item) => item.id !== form.id));
    } catch (deleteError) {
      setError(errorMessage(deleteError));
    } finally {
      setActionId(null);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        className="mb-0"
        breadcrumb={[{ label: t('set.settings'), to: '/settings' }, { label: t('set.ticketForms') }]}
        title={t('set.ticketForms')}
        description={t('set.tfm.pageDescription')}
        actions={(
          <Button onClick={() => void createForm()} disabled={creating}>
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            {creating ? t('set.tfm.creating') : t('set.tfm.newForm')}
          </Button>
        )}
      />

      {error && <ErrorState message={error} onRetry={() => void loadForms()} />}

      <Card>
        <CardHeader className="flex-row flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base">{t('set.tfm.allForms')}</CardTitle>
            <p className="mt-1 text-xs text-ink-subtle">{t(forms.length === 1 ? 'set.tfm.configuredOne' : 'set.tfm.configuredMany', { count: forms.length })}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowFilters((current) => !current)} aria-expanded={showFilters}>
              <Filter className="mr-2 h-4 w-4" aria-hidden="true" />
              {t('set.tfm.filter')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => void loadForms()} disabled={loading || actionId !== null}>
              <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
              {t('set.refresh')}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {showFilters && (
            <div className="grid gap-3 rounded-md border bg-surface-hover p-3 md:grid-cols-[minmax(0,1fr)_12rem]">
              <Input
                aria-label={t('set.tfm.searchAria')}
                placeholder={t('set.tfm.searchPlaceholder')}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <label className="text-sm text-ink-muted">
                <span className="mb-1 block font-medium">{t('set.tfm.status')}</span>
                <DropdownSelect
                  options={[
                    { value: 'all', label: t('set.tfm.allStatuses') },
                    { value: 'published', label: t('set.published') },
                    { value: 'draft', label: t('set.draft') },
                  ]}
                  value={statusFilter}
                  onChange={(value) => setStatusFilter(value as StatusFilter)}
                  searchable={false}
                />
              </label>
            </div>
          )}

          {!showFilters && (
            <div className="relative max-w-md">
              <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-ink-subtle" aria-hidden="true" />
              <Input
                aria-label={t('set.tfm.searchAria')}
                placeholder={t('set.tfm.searchPlaceholder')}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="pl-9"
              />
            </div>
          )}

          {loading ? (
            <SkeletonRow rows={4} />
          ) : visibleForms.length === 0 ? (
            forms.length ? (
              <EmptyState
                title={t('set.tfm.emptyFilterTitle')}
                description={t('set.tfm.emptyFilterDesc')}
              />
            ) : (
              <EmptyState
                title={t('set.tfm.emptyFirstTitle')}
                description={t('set.tfm.emptyFirstDesc')}
                action={<Button onClick={() => void createForm()} disabled={creating}>{t('set.tfm.createFirst')}</Button>}
              />
            )
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('set.tfm.colName')}</TableHead>
                    <TableHead>{t('set.tfm.colCreatedAt')}</TableHead>
                    <TableHead>{t('set.tfm.colUpdatedBy')}</TableHead>
                    <TableHead>{t('set.tfm.colUpdatedAt')}</TableHead>
                    <TableHead>{t('set.tfm.colPortals')}</TableHead>
                    <TableHead><span className="sr-only">{t('set.tfm.colActions')}</span></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleForms.map((form) => (
                    <TableRow key={form.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Link to={`/settings/ticket-forms/${form.id}`} className="font-medium text-brand-700 hover:underline">
                            {form.name}
                          </Link>
                          {form.isDefault && (
                            <Badge variant="outline" className="inline-flex items-center gap-1">
                              <LockKeyhole className="h-3 w-3" aria-hidden="true" />
                              {t('set.default')}
                            </Badge>
                          )}
                          <Badge variant={form.isPublished ? 'success' : 'outline'}>{form.isPublished ? t('set.published') : t('set.draft')}</Badge>
                        </div>
                        {form.description && <p className="mt-1 max-w-sm truncate text-xs text-ink-subtle">{form.description}</p>}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-ink-muted">{formatDate(form.createdAt, language, user?.timezone)}</TableCell>
                      <TableCell className="font-mono text-xs text-ink-muted">{updatedByLabel(form.updatedBy)}</TableCell>
                      <TableCell className="whitespace-nowrap text-ink-muted">{formatDate(form.updatedAt, language, user?.timezone)}</TableCell>
                      <TableCell className="text-ink-subtle">{t('set.tfm.notConfigured')}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger
                            className="rounded-md p-2 text-ink-subtle hover:bg-surface-subtle hover:text-ink"
                            aria-label={t('set.tfm.actionsFor', { name: form.name })}
                          >
                            <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem onClick={() => navigate(`/settings/ticket-forms/${form.id}`)}>
                              <Pencil className="mr-2 h-4 w-4" aria-hidden="true" /> {t('set.tfm.editForm')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => void togglePublished(form)} disabled={actionId === form.id}>
                              {form.isPublished
                                ? <><EyeOff className="mr-2 h-4 w-4" aria-hidden="true" /> {t('set.unpublish')}</>
                                : <><Eye className="mr-2 h-4 w-4" aria-hidden="true" /> {t('set.publish')}</>}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => void removeForm(form)} disabled={form.isDefault || actionId === form.id}>
                              <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" /> {t('set.deleteForm')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
